import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";

/**
 * Compact single-line input that opens a larger floating textarea while
 * hovered or focused, so long text can be edited comfortably from a tight
 * table cell. Both fields edit the same value; the popover closes on
 * mouse-leave (unless the textarea is focused), blur, or Escape.
 */
const HoverExpandInput = ({ value, onChange, placeholder, className = "tdf-input", inputStyle = {} }) => {
    const anchorRef = useRef(null);
    const openTimer = useRef(null);
    const closeTimer = useRef(null);
    const editorFocused = useRef(false);
    const [pos, setPos] = useState(null);

    useEffect(() => () => { clearTimeout(openTimer.current); clearTimeout(closeTimer.current); }, []);

    const openNow = () => {
        const el = anchorRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const width = Math.max(320, Math.min(480, Math.round(r.width * 1.6)));
        const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 16));
        const openUp = r.bottom + 200 > window.innerHeight && r.top > window.innerHeight - r.bottom;
        setPos({ left, top: openUp ? r.top - 6 : r.bottom + 6, openUp, width });
    };

    const scheduleOpen = () => {
        clearTimeout(closeTimer.current);
        if (pos) return;
        clearTimeout(openTimer.current);
        openTimer.current = setTimeout(openNow, 180);
    };

    const scheduleClose = () => {
        clearTimeout(openTimer.current);
        clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => {
            if (!editorFocused.current) setPos(null);
        }, 160);
    };

    const cancelClose = () => clearTimeout(closeTimer.current);

    return (
        <span
            ref={anchorRef}
            style={{ display: "block", flex: 1, minWidth: 0 }}
            onMouseEnter={scheduleOpen}
            onMouseLeave={scheduleClose}
        >
            <input
                type="text"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className={className}
                style={inputStyle}
                onFocus={scheduleOpen}
            />
            {pos && ReactDOM.createPortal(
                <div
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                    style={{
                        position: "fixed",
                        left: pos.left,
                        top: pos.top,
                        transform: pos.openUp ? "translateY(-100%)" : "none",
                        zIndex: 10060,
                        width: pos.width,
                        background: "#fff",
                        border: "1.5px solid #c3d1fb",
                        borderRadius: 10,
                        boxShadow: "0 10px 30px rgba(15,23,42,0.18)",
                        padding: 8,
                    }}
                >
                    <textarea
                        value={value ?? ""}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder}
                        rows={5}
                        onFocus={() => { editorFocused.current = true; cancelClose(); }}
                        onBlur={() => { editorFocused.current = false; scheduleClose(); }}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") { editorFocused.current = false; setPos(null); }
                        }}
                        style={{
                            width: "100%",
                            minHeight: 110,
                            maxHeight: 240,
                            resize: "vertical",
                            border: "1px solid #e2e8f0",
                            borderRadius: 7,
                            padding: "8px 10px",
                            fontSize: 13,
                            lineHeight: 1.55,
                            color: "#1e293b",
                            fontFamily: "'DM Sans','Segoe UI',sans-serif",
                            outline: "none",
                            boxSizing: "border-box",
                        }}
                    />
                </div>,
                document.body
            )}
        </span>
    );
};

export default HoverExpandInput;
